import { createSlice } from '@reduxjs/toolkit';
import { OUTPUT_CATEGORIES } from '../config/panelConfig/outputCategories';
import { OUTPUT_TYPES } from '../config/panelConfig/outputTypes';
import { PANEL_TYPES } from '../config/panelConfig/panelTypes';
import { PANEL_LEVEL } from '../config/panelConfig/panelTypes';

const initialState = {
  panelTypes: { ...PANEL_TYPES },
  outputTypes: { ...OUTPUT_TYPES },
  outputCategories: { ...OUTPUT_CATEGORIES },
  customizations: {},
};

const panelConfigSlice = createSlice({
  name: 'panelConfig',
  initialState,
  reducers: {
    // Panel Type CRUD
    addPanelType(state, action) {
      const pt = action.payload;
      state.panelTypes[pt.id] = pt;
    },
    updatePanelType(state, action) {
      const { id, ...changes } = action.payload;
      if (state.panelTypes[id]) {
        Object.assign(state.panelTypes[id], changes);
      }
    },
    removePanelType(state, action) {
      delete state.panelTypes[action.payload];
    },

    // Output Type CRUD
    addOutputType(state, action) {
      const ot = action.payload;
      state.outputTypes[ot.id] = ot;
    },
    updateOutputType(state, action) {
      const { id, ...changes } = action.payload;
      if (state.outputTypes[id]) {
        Object.assign(state.outputTypes[id], changes);
      }
    },
    removeOutputType(state, action) {
      delete state.outputTypes[action.payload];
    },

    // Panel outputs & child panels
    updatePanelOutputs(state, action) {
      const { panelTypeId, defaultOutputs } = action.payload;
      if (state.panelTypes[panelTypeId]) {
        state.panelTypes[panelTypeId].defaultOutputs = defaultOutputs;
      }
    },
    updateAllowedChildPanels(state, action) {
      const { panelTypeId, allowedChildPanels } = action.payload;
      if (state.panelTypes[panelTypeId]) {
        state.panelTypes[panelTypeId].allowedChildPanels = allowedChildPanels;
      }
    },

    // Reset
    resetToDefaults(state) {
      state.panelTypes = { ...PANEL_TYPES };
      state.outputTypes = { ...OUTPUT_TYPES };
      state.outputCategories = { ...OUTPUT_CATEGORIES };
      state.customizations = {};
    },
  },
});

export const {
  addPanelType,
  updatePanelType,
  removePanelType,
  addOutputType,
  updateOutputType,
  removeOutputType,
  updatePanelOutputs,
  updateAllowedChildPanels,
  resetToDefaults,
} = panelConfigSlice.actions;

// --- Selectors ---

export const selectPanelTypes = (state) => state.panelConfig.panelTypes;
export const selectOutputTypes = (state) => state.panelConfig.outputTypes;
export const selectOutputCategories = (state) => state.panelConfig.outputCategories;

export const selectPrimaryPanelTypes = (state) => {
  const pts = state.panelConfig.panelTypes;
  return Object.values(pts).filter(pt => pt.level === PANEL_LEVEL.PRIMARY);
};

export const selectAllowedChildPanels = (panelTypeId) => (state) => {
  const pt = state.panelConfig.panelTypes[panelTypeId];
  if (!pt) return [];
  return pt.allowedChildPanels
    .map(childId => state.panelConfig.panelTypes[childId])
    .filter(Boolean);
};

export const selectOutputTypesByCategory = (categoryId) => (state) => {
  const ots = state.panelConfig.outputTypes;
  return Object.values(ots).filter(ot => ot.categoryId === categoryId);
};

export const selectPanelMenuItems = (panelTypeId) => (state) => {
  const pt = state.panelConfig.panelTypes[panelTypeId];
  if (!pt) return { outputGroups: [], panelOutputs: [] };

  const outputTypes = state.panelConfig.outputTypes;
  const categories = state.panelConfig.outputCategories;

  const outputGroups = {};
  const panelOutputs = [];

  pt.defaultOutputs.forEach(outputId => {
    const ot = outputTypes[outputId];
    if (!ot) return;

    if (ot.categoryId === 'PANEL') {
      panelOutputs.push(ot);
    } else {
      const cat = categories[ot.categoryId];
      if (!outputGroups[ot.categoryId]) {
        outputGroups[ot.categoryId] = {
          category: cat,
          items: [],
        };
      }
      outputGroups[ot.categoryId].items.push(ot);
    }
  });

  const sortedGroups = Object.values(outputGroups).sort(
    (a, b) => a.category.order - b.category.order
  );

  return { outputGroups: sortedGroups, panelOutputs };
};

export default panelConfigSlice.reducer;
